/**
 * `<LockedProgressNoteSections>` — LD-21 follow-up.
 *
 * Read-only renderer for the five-section structured Progress Note used by
 * `<ProgressNoteViewer>` when the locked note is scribe-backed. Hydrates form
 * state from the existing `QuestionnaireResponse` + `Claim` cascade and mounts
 * the same section components the edit form uses, with `readOnly={true}`.
 *
 * Why a thin wrapper instead of `<ProgressNoteEdit readOnly>`:
 *   - `<ProgressNoteEdit>` mounts the Editing-Note action bar, Submit button,
 *     Save handler, Cancel handler, BillingSection edit modal, etc. None of
 *     those belong on a locked-note read view.
 *   - The five section components (`EncounterDetailsSection` /
 *     `SubjectiveSection` / `ObjectiveSection` / `AssessmentSection` /
 *     `PlanSection`) already consume RHF form context and honor a `readOnly`
 *     prop. We can mount them directly inside our own `<FormProvider>` for
 *     reuse without duplicating the section render logic.
 *
 * What's hydrated (from the linked QR + Claim + DocumentReference):
 *   - QR narrative answers → `summary.chiefComplaint / hpi / assessment / plan`.
 *   - QR `pastMedicalHistory` + `assessment1` Codings → `conditions[]`
 *     (encounter-diagnosis first with rank, problem-list-item after).
 *   - QR `medsReviewed` + `treatmentsPrescribed` Codings → `medications[]`
 *     (continuation + new-prescription intents).
 *   - QR `testsOrdered` Codings → `serviceRequests[]` (category inferred
 *     from system: CPT → imaging, LOINC → laboratory).
 *   - QR `allergies` Codings → `allergies[]`.
 *   - QR typed vital answers (`bpSystolic`, `hr`, `wt`, …) → LOINC-coded
 *     `observations[]` so the Vitals table renders rows.
 *   - Blob `reviewOfSystemsStructured` → `summary.ros` (ROS table; legacy → `[]`).
 *   - Blob `followUp` string → `followUps[]` (Follow-Up section; legacy → `[]`).
 *   - Blob `mdm` + `billingSuggestion.totalTime` → the MDM / Total Time section.
 *   - Blob `objective` → trailing `exam` ObservationItem (doctor's prose).
 *   - `visitBillingGet` (the Claim's codes merged with the note's
 *     `ai-billing-suggestion` extension) → `billingSuggestion` for the MDM's level
 *     of service and duration; the billing summary itself reads the visit directly.
 */
import { useEffect, useMemo, useState } from 'react'
import { Paper, Stack } from '@mantine/core'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMedplum } from '@medplum/react'
import type {
  DocumentReference,
  Encounter,
  Provenance,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes'
import {
  AssessmentSection,
  EncounterDetailsSection,
  AiDisclosureSection,
  FollowUpSection,
  MdmAndTimeSection,
  ObjectiveSection,
  PlanSection,
  SubjectiveSection,
} from '../ProgressNoteEdit/ProgressNoteSections'
import {
  makeEmptyProgressNoteFormValues,
  progressNoteEditFormSchema,
  type ObservationItem,
  type ProgressNoteEditFormValues,
} from '../ProgressNoteEdit/progressNoteEditSchema'
import { toFormBillingSuggestion } from '@/components/billing'
import { useOrpc } from '@/hooks/useClient'
import {
  hydrateAllergiesFromQr,
  hydrateConditionsFromQr,
  hydrateMedicationsFromQr,
  hydrateServiceRequestsFromQr,
  hydrateVitalObservationsFromQr,
} from '../ProgressNoteEdit/progressNoteQrHydrate'
import {
  buildExamNarrativeRow,
  hydrateAllergiesFromScribeDraft,
  hydrateConditionsFromScribeDraft,
  hydrateExamNarrativeFromScribeDraft,
  hydrateFamilyHistoryFromScribeDraft,
  hydrateFollowUpsFromScribeDraft,
  hydrateMedicationsFromScribeDraft,
  hydrateRosFromScribeDraft,
  hydrateTimeEntriesFromScribeDraft,
  hydrateServiceRequestsFromScribeDraft,
  hydrateSummaryFromScribeDraft,
  hydrateVitalObservationsFromScribeDraft,
  readScribeDraftPayload,
  splitHpiParagraphs,
} from '../ProgressNoteEdit/scribeDraftPayload'
import { logError } from '@/utils/errorUtils'
import { formatDateTime, toClinicDateTimeLocal } from '@/utils'
import type { NoteSignature } from '@/utils/provenance'
import { ElectronicSignature } from './ElectronicSignature'

type LockedProgressNoteSectionsProps = {
  documentReference: DocumentReference
  provenance?: Provenance
  /** Backs the read-only "Encounter Date" cell from `period.start`. */
  encounter?: Encounter
  /**
   * The encounter's id as the viewer knows it before `encounter` has loaded.
   * The charge quote keys on it so the signer is never quoted while the
   * Encounter is in flight.
   */
  encounterId?: string
  patientId?: string
  /** Present only for a signed note (PD-1897); closes the note below the disclosure. */
  signature?: NoteSignature
}

function inferVisitType(documentReference: DocumentReference): 'new' | 'follow-up' {
  const typeText = `${documentReference.type?.text || ''} ${documentReference.type?.coding
    ?.map((coding) => coding.display || '')
    .join(' ')}`.toLowerCase()
  return typeText.includes('new') ? 'new' : 'follow-up'
}

function getProvenanceTargetRef(provenance: Provenance | undefined, prefix: string) {
  return provenance?.target
    ?.map((target) => target.reference)
    .find((reference) => reference?.startsWith(`${prefix}/`))
}

function getQuestionnaireStringAnswer(
  response: QuestionnaireResponse | undefined,
  linkId: string,
): string {
  if (!response?.item?.length) {
    return ''
  }
  const queue: QuestionnaireResponseItem[] = [...response.item]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    if (current.linkId === linkId) {
      return current.answer?.[0]?.valueString || ''
    }
    if (current.item?.length) {
      queue.push(...current.item)
    }
  }
  return ''
}

export function LockedProgressNoteSections({
  documentReference,
  provenance,
  encounter,
  encounterId,
  patientId,
  signature,
}: LockedProgressNoteSectionsProps) {
  const medplum = useMedplum()

  // Mirrors `NoteMetadataGrid.resolveEncounterDate` — encounter date or `—`.
  const encounterDateDisplay = useMemo(() => {
    const start = encounter?.period?.start
    return start ? formatDateTime(start, '-', true) : '—'
  }, [encounter?.period?.start])

  const initialNoteDate = useMemo(
    () => toClinicDateTimeLocal(documentReference.date),
    [documentReference.date],
  )
  const initialVisitType = useMemo(() => inferVisitType(documentReference), [documentReference])

  const [hydrated, setHydrated] = useState(false)

  const methods = useForm<ProgressNoteEditFormValues>({
    resolver: zodResolver(
      progressNoteEditFormSchema,
    ) as unknown as import('react-hook-form').Resolver<ProgressNoteEditFormValues>,
    defaultValues: makeEmptyProgressNoteFormValues(initialNoteDate, initialVisitType),
    mode: 'onSubmit',
  })

  // RHF field-array hooks the section components consume. Read-only sections
  // still need these wired because the sections destructure `.fields` / `.remove`
  // off the return value even when those mutating handlers go unused in read
  // mode.
  const conditionsField = useFieldArray({ control: methods.control, name: 'conditions' })
  const medicationsField = useFieldArray({ control: methods.control, name: 'medications' })
  const serviceRequestsField = useFieldArray({ control: methods.control, name: 'serviceRequests' })
  const followUpsField = useFieldArray({ control: methods.control, name: 'followUps' })

  // The billing summary and its editor read and write the visit's billing themselves;
  // the form only needs the encounter they key on.
  const stableEncounterId = encounter?.id ?? encounterId
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  useEffect(() => {
    let active = true

    const hydrate = async () => {
      if (!provenance) {
        if (active) setHydrated(true)
        return
      }

      const questionnaireRef = getProvenanceTargetRef(provenance, 'QuestionnaireResponse')
      const questionnaireId = questionnaireRef?.split('/')[1]
      let qrPayload: QuestionnaireResponse | undefined
      if (questionnaireId) {
        try {
          qrPayload = await medplum.readResource('QuestionnaireResponse', questionnaireId)
        } catch (err) {
          logError(
            'progress-note-viewer',
            'Failed to load questionnaire response for locked sections',
            err,
          )
        }
      }

      // The visit's billing as every surface reads it: the AI's suggestion merged with
      // what the Claim bills. A failed read leaves the MDM's level of service blank
      // rather than blocking the rest of the note.
      let visitBillingSuggestion: Parameters<typeof toFormBillingSuggestion>[0]
      if (stableEncounterId) {
        try {
          const visitBilling = await queryClient.fetchQuery(
            orpc.visitBillingGet.queryOptions({ input: { encounterId: stableEncounterId } }),
          )
          visitBillingSuggestion = visitBilling.billingSuggestion
        } catch (err) {
          logError('progress-note-viewer', 'Failed to load visit billing for locked sections', err)
        }
      }

      if (!active) return

      // Hydrate every section blob-first (QR fallback for legacy / non-scribe):
      // the blob is on the readable DocRef, so the QR-only narratives + vitals
      // survive when an org-scoped practitioner can't read the draft QR.
      const scribeDraft = readScribeDraftPayload(documentReference)

      const {
        examReason,
        prob,
        objective,
        assessment: assessmentNarrative,
        plan,
        mdm,
      } = scribeDraft
        ? hydrateSummaryFromScribeDraft(scribeDraft.soapNote)
        : {
            examReason: getQuestionnaireStringAnswer(qrPayload, 'examReason'),
            prob: getQuestionnaireStringAnswer(qrPayload, 'prob'),
            objective: getQuestionnaireStringAnswer(qrPayload, 'objective'),
            assessment: getQuestionnaireStringAnswer(qrPayload, 'assessment'),
            plan: getQuestionnaireStringAnswer(qrPayload, 'plan'),
            // No QR slot — MDM only ever existed on the blob (PD-1321).
            mdm: '',
          }

      const conditions = scribeDraft
        ? hydrateConditionsFromScribeDraft(scribeDraft.soapNote)
        : hydrateConditionsFromQr(qrPayload)
      const medications = scribeDraft
        ? hydrateMedicationsFromScribeDraft(scribeDraft.soapNote)
        : hydrateMedicationsFromQr(qrPayload)
      const serviceRequests = scribeDraft
        ? hydrateServiceRequestsFromScribeDraft(scribeDraft.soapNote)
        : hydrateServiceRequestsFromQr(qrPayload)
      const allergies = scribeDraft
        ? hydrateAllergiesFromScribeDraft(scribeDraft.soapNote)
        : hydrateAllergiesFromQr(qrPayload)
      // ROS from the blob's structured slot; legacy / QR-only → `[]`.
      const ros = scribeDraft ? hydrateRosFromScribeDraft(scribeDraft.soapNote) : []
      // Family History from the blob's structured slot (PD-1377); legacy → `[]`.
      const familyHistory = scribeDraft
        ? hydrateFamilyHistoryFromScribeDraft(scribeDraft.soapNote)
        : []
      // Follow-ups from the blob's `followUp` slot; legacy / QR-only → `[]`.
      const followUps = scribeDraft ? hydrateFollowUpsFromScribeDraft(scribeDraft.soapNote) : []
      const vitalObservations = scribeDraft
        ? hydrateVitalObservationsFromScribeDraft(scribeDraft.soapNote)
        : hydrateVitalObservationsFromQr(qrPayload)
      const examNarrative = scribeDraft
        ? hydrateExamNarrativeFromScribeDraft(scribeDraft.soapNote)
        : objective
      const observations: ObservationItem[] = [
        ...vitalObservations,
        ...buildExamNarrativeRow(examNarrative),
      ]

      const billingSuggestion = toFormBillingSuggestion(visitBillingSuggestion)
      const emptyForm = makeEmptyProgressNoteFormValues(initialNoteDate, initialVisitType)
      // Total Time rows live on the blob, not the billing extension (PD-1321).
      const timeEntries = scribeDraft
        ? hydrateTimeEntriesFromScribeDraft(scribeDraft.soapNote)
        : { providerTimeEntries: [], careCoordinatorTimeEntries: [] }

      methods.reset({
        ...emptyForm,
        noteDate: initialNoteDate,
        visitType: initialVisitType,
        aiAssisted: scribeDraft?.soapNote.aiAssisted === true,
        encounterLanguage: scribeDraft?.soapNote.encounterLanguage,
        summary: {
          chiefComplaint: examReason,
          hpi: splitHpiParagraphs(prob),
          ros,
          assessment: assessmentNarrative,
          plan,
          mdm,
        },
        conditions,
        medications,
        serviceRequests,
        allergies,
        familyHistory,
        observations,
        followUps,
        billingSuggestion: {
          ...(billingSuggestion ?? emptyForm.billingSuggestion),
          ...timeEntries,
        },
      })

      if (active) setHydrated(true)
    }

    void hydrate()

    return () => {
      active = false
    }
    // `documentReference` belongs here: the billing projection reads its
    // `ai-billing-suggestion` extension, so keying re-hydration on `provenance`
    // identity alone could render a stale extension after a billing save.
  }, [
    medplum,
    provenance,
    documentReference,
    methods,
    initialNoteDate,
    initialVisitType,
    orpc,
    queryClient,
    stableEncounterId,
  ])

  // After a billing save (a chip, the editor) the MDM's level of service and duration
  // follow the server; Total Time rows stay as the blob hydrated them.
  const visitBilling = useQuery(
    orpc.visitBillingGet.queryOptions({
      input: { encounterId: stableEncounterId ?? '' },
      enabled: hydrated && Boolean(stableEncounterId),
    }),
  )
  useEffect(() => {
    const suggestion = toFormBillingSuggestion(visitBilling.data?.billingSuggestion)
    if (!hydrated || !suggestion) return

    const current = methods.getValues('billingSuggestion')
    methods.setValue('billingSuggestion', {
      ...suggestion,
      providerTimeEntries: current.providerTimeEntries,
      careCoordinatorTimeEntries: current.careCoordinatorTimeEntries,
    })
  }, [visitBilling.data, hydrated, methods])

  // Avoid rendering empty section shells before the QR + Claim resolve. The
  // empty defaults would briefly flash "—" placeholders. Once hydrated, the
  // section components handle their own empty-state messaging.
  if (!hydrated) return null

  return (
    <FormProvider {...methods}>
      <Paper p="md" withBorder data-testid="locked-progress-note-sections">
        <Stack gap="md">
          <EncounterDetailsSection
            encounterDate={initialNoteDate || new Date().toISOString()}
            visitType={initialVisitType === 'new' ? 'Initial Visit' : 'Follow-up Visit'}
            description={
              documentReference.description || documentReference.type?.text || 'Telehealth visit'
            }
            readOnlyDateDisplay={encounterDateDisplay}
            readOnly
          />

          <SubjectiveSection readOnly patientId={patientId} />

          <ObjectiveSection readOnly />

          <AssessmentSection readOnly conditionsField={conditionsField} />

          <PlanSection
            readOnly
            docStatus={documentReference.docStatus as 'preliminary' | 'final' | undefined}
            serviceRequestsField={serviceRequestsField}
            medicationsField={medicationsField}
            // Billing stays editable at every Claim Status (PD-1689); without this
            // the read-only section would hide the Edit Billing affordance.
            billingEditable
            encounterId={stableEncounterId}
          />

          {/* PD-1321 closing order: MDM → Total Time → Follow-Up. */}
          <MdmAndTimeSection readOnly />
          <FollowUpSection readOnly followUpsField={followUpsField} />
          <AiDisclosureSection />
          {signature && <ElectronicSignature signature={signature} />}
        </Stack>
      </Paper>
    </FormProvider>
  )
}
